import React, { useState, useEffect, useCallback, useRef } from 'react';
import Box from '@mui/material/Box';
import ImageList from '@mui/material/ImageList';
import ImageListItem from '@mui/material/ImageListItem';
import Modal from '@mui/material/Modal';
import Backdrop from '@mui/material/Backdrop';
import Fade from '@mui/material/Fade';
import IconButton from '@mui/material/IconButton';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';

interface ImageSliderProps {
  images: string[];
}

const ImageSlider: React.FC<ImageSliderProps> = ({ images }) => {
  const [open, setOpen] = useState(false);
  const [currentIndex, setCurrentIndex] = useState<number>(0);

  // --- Responsive cols ---
  const getCols = () => {
    if (window.innerWidth < 480) return 2;
    return 3;
  };
  const [cols, setCols] = useState(getCols());

  useEffect(() => {
    const handleResize = () => setCols(getCols());
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // --- Open / Close ---
  const handleOpen = (index: number) => {
    setCurrentIndex(index);
    setOpen(true);
  };
  const handleClose = () => setOpen(false);

  // --- Navigation ---
  const prevImage = useCallback(() => {
    if (!images.length) return;
    setCurrentIndex((i) => (i - 1 + images.length) % images.length);
  }, [images.length]);

  const nextImage = useCallback(() => {
    if (!images.length) return;
    setCurrentIndex((i) => (i + 1) % images.length);
  }, [images.length]);

  // --- Keyboard navigation ---
  useEffect(() => {
    if (!open) return;

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'ArrowLeft') prevImage();
      if (e.key === 'ArrowRight') nextImage();
      if (e.key === 'Escape') handleClose();
    };

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [open, prevImage, nextImage]);

  // --- Drag/Swipe (Pointer Events: desktop + mobile) ---
  const [dragX, setDragX] = useState(0);
  const startXRef = useRef<number | null>(null);
  const draggingRef = useRef(false);
  const pointerIdRef = useRef<number | null>(null);

  const SWIPE_THRESHOLD = 60; // px

  const onPointerDown = (e: React.PointerEvent) => {
    if (images.length <= 1) return;
    e.stopPropagation();

    draggingRef.current = true;
    startXRef.current = e.clientX;
    pointerIdRef.current = e.pointerId;
    setDragX(0);

    (e.currentTarget as HTMLElement).setPointerCapture?.(e.pointerId);
  };

  const onPointerMove = (e: React.PointerEvent) => {
    if (!draggingRef.current || startXRef.current === null) return;
    if (pointerIdRef.current !== null && e.pointerId !== pointerIdRef.current) return;

    e.stopPropagation();
    e.preventDefault(); // important on mobile

    const delta = e.clientX - startXRef.current;
    setDragX(delta);
  };

  const endSwipe = (e: React.PointerEvent) => {
    if (!draggingRef.current) return;
    if (pointerIdRef.current !== null && e.pointerId !== pointerIdRef.current) return;

    e.stopPropagation();
    e.preventDefault();

    const delta = dragX;

    draggingRef.current = false;
    startXRef.current = null;
    pointerIdRef.current = null;

    setDragX(0);

    if (images.length <= 1) return;
    if (Math.abs(delta) < SWIPE_THRESHOLD) return;

    if (delta > 0) prevImage();
    else nextImage();
  };

  const selectedImage = images[currentIndex] || '';

  return (
    <>
      <Box sx={{ width: '100%', maxWidth: 1000, height: 'auto' }}>
        <ImageList variant="masonry" cols={cols} gap={8} sx={{ overflow: 'hidden' }}>
          {images.map((image, index) => (
            <ImageListItem
              key={index}
              onClick={() => handleOpen(index)}
              sx={{
                cursor: 'pointer',
                overflow: 'hidden',
                borderRadius: '8px',
                transition: 'box-shadow 0.3s ease',
                '&:hover': {
                  boxShadow: '0 10px 20px rgba(0,0,0,0.2)',
                },
                '&:hover img': {
                  transform: 'scale(1.05)',
                },
              }}
            >
              <img
                src={`${image}?w=248&fit=crop&auto=format`}
                srcSet={`${image}?w=248&fit=crop&auto=format&dpr=2 2x`}
                alt={`Gallery item ${index + 1}`}
                loading="lazy"
                draggable={false}
                style={{
                  display: 'block',
                  width: '100%',
                  borderRadius: '8px',
                  transition: 'transform 0.3s ease',
                }}
              />
            </ImageListItem>
          ))}
        </ImageList>
      </Box>

      <Modal
        open={open}
        onClose={handleClose}
        closeAfterTransition
        slots={{ backdrop: Backdrop }}
        slotProps={{
          backdrop: {
            timeout: 500,
            sx: { backgroundColor: 'rgba(0, 0, 0, 0.9)' },
          },
        }}
      >
        <Fade in={open}>
          {/* Click sull'overlay chiude */}
          <Box
            onClick={handleClose}
            sx={{
              position: 'relative',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              height: '100%',
              width: '100%',
              outline: 'none',
              zIndex: 1301,
            }}
          >
            {/* Freccia sinistra */}
            {images.length > 1 && (
              <IconButton
                onClick={(e) => {
                  e.stopPropagation();
                  prevImage();
                }}
                aria-label="Immagine precedente"
                sx={{
                  position: 'absolute',
                  left: { xs: 8, sm: 24 },
                  top: '50%',
                  transform: 'translateY(-50%)',
                  color: 'white',
                  backgroundColor: 'rgba(0,0,0,0.35)',
                  '&:hover': { backgroundColor: 'rgba(0,0,0,0.55)' },
                  zIndex: 1303,
                }}
              >
                <ArrowBackIosNewIcon />
              </IconButton>
            )}

            {/* Area immagine con drag/swipe (click NON chiude) */}
            <Box
              onClick={(e) => e.stopPropagation()}
              onPointerDown={onPointerDown}
              onPointerMove={onPointerMove}
              onPointerUp={endSwipe}
              onPointerCancel={endSwipe}
              sx={{
                zIndex: 1302,
                touchAction: 'none',
                userSelect: 'none',
                cursor: images.length > 1 ? (dragX !== 0 ? 'grabbing' : 'grab') : 'default',
                maxWidth: '90vw',
                maxHeight: '90vh',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <img
                src={selectedImage}
                alt="Full screen"
                draggable={false}
                style={{
                  maxHeight: '90vh',
                  maxWidth: '90vw',
                  borderRadius: '8px',
                  display: 'block',
                  transform: `translateX(${dragX}px)`,
                  transition: draggingRef.current ? 'none' : 'transform 150ms ease',
                  pointerEvents: 'none', // gli eventi vanno al Box
                }}
              />
            </Box>

            {/* Freccia destra */}
            {images.length > 1 && (
              <IconButton
                onClick={(e) => {
                  e.stopPropagation();
                  nextImage();
                }}
                aria-label="Immagine successiva"
                sx={{
                  position: 'absolute',
                  right: { xs: 8, sm: 24 },
                  top: '50%',
                  transform: 'translateY(-50%)',
                  color: 'white',
                  backgroundColor: 'rgba(0,0,0,0.35)',
                  '&:hover': { backgroundColor: 'rgba(0,0,0,0.55)' },
                  zIndex: 1303,
                }}
              >
                <ArrowForwardIosIcon />
              </IconButton>
            )}
          </Box>
        </Fade>
      </Modal>
    </>
  );
};

export default ImageSlider;
